/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState } from 'react';
import * as Yup from 'yup';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { FormikProvider, useFormik } from 'formik';
import { getUserByToken, login } from '../core/_requests';
import { useAuth } from '../core/Auth';

const loginSchema = Yup.object().shape({
  userName: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
  password: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
});

const initialValues = {
  userName: 'ho.admin@saawree.com',
  password: '123Pa$$word!',
};

/*
  Formik+YUP+Typescript:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
  https://medium.com/@maurice.de.beijer/yup-validation-and-typescript-and-formik-6c342578a20e
*/

export function Login() {
  const [loading, setLoading] = useState(false);
  const { saveAuth, setCurrentUser } = useAuth();

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true);
      try {
        const { data: auth } = await login(values.userName, values.password);
        const minimalAuth = {
          data: {
            token: auth.data.token,
            refreshToken: auth.data.refreshToken,
            refreshTokenExpiryTime: auth.data.refreshTokenExpiryTime,
          },
        };
        saveAuth(minimalAuth);
        const { data: user } = await getUserByToken(auth.data.token);
        setCurrentUser(user);
      } catch (error) {
        saveAuth(undefined);
        setStatus('The login details are incorrect');
        setSubmitting(false);
        setLoading(false);
      }
    },
  });

  const [showGeneratedPassword, setShowGeneratedPassword] = useState(false);
  const handleTogglePasswordVisibility = () => {
    setShowGeneratedPassword(!showGeneratedPassword);
  };
  return (
    <FormikProvider value={formik}>
      <form
        className='form w-100'
        onSubmit={formik.handleSubmit}
        noValidate
        id='kt_login_signin_form'
      >
        {/* begin::Heading */}
        <div className='text-center mb-11'>
          <h1 className='text-dark fw-bolder mb-3'>Sign In</h1>
        </div>
        {/* begin::Heading */}

        {formik.status ? (
          <div className='mb-lg-15 alert alert-danger'>
            <div className='alert-text font-weight-bold'>{formik.status}</div>
          </div>
        ) : (
          ''
        )}

        {/* begin::Form group */}
        <div className='fv-row mb-8'>
          <label className='form-label fs-6 fw-bolder text-dark'>UserName</label>
          <input
            placeholder='Email & phone number '
            {...formik.getFieldProps('userName')}
            className={clsx(
              'form-control bg-transparent',
              { 'is-invalid': formik.touched.userName && formik.errors.userName },
              {
                'is-valid': formik.touched.userName && !formik.errors.userName,
              }
            )}
            type='text'
            name='userName'
            autoComplete='off'
            onChange={(e) => formik.setFieldValue('userName', e.target.value)}
          />
          {formik.touched.userName && formik.errors.userName && (
            <div className='fv-plugins-message-container'>
              <span role='alert'>{formik.errors.userName}</span>
            </div>
          )}
        </div>
        {/* end::Form group */}

        {/* begin::Form group */}
        <div className='fv-row mb-3'>
          <label className='form-label fw-bolder text-dark fs-6 mb-0'>Password</label>

          <div className='input-group'>
            <input
              placeholder='password'
              {...formik.getFieldProps('password')}
              type={showGeneratedPassword ? 'text' : 'password'}
              name='password'
              className={clsx(
                'form-control bg-transparent',
                {
                  'is-invalid': formik.touched.password && formik.errors.password,
                },
                {
                  'is-valid': formik.touched.password && !formik.errors.password,
                }
              )}
              onChange={(e) => formik.setFieldValue('password', e.target.value)}
            />
            <div className='input-group-append'>
              <span
                className={clsx(
                  'ki-duotone cursor-pointer position-absolute translate-middle top-50 end-0 me-5 fs-1',
                  showGeneratedPassword ? 'ki-eye-slash' : 'ki-eye'
                )}
                onClick={handleTogglePasswordVisibility}
              >
                <span className='path1'></span>
                <span className='path2'></span>
                <span className='path3'></span>
              </span>
            </div>
          </div>
          {formik.touched.password && formik.errors.password && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.password}</span>
              </div>
            </div>
          )}
        </div>
        {/* end::Form group */}

        {/* begin::Wrapper */}
        <div className='d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold mb-8'>
          <div />

          {/* begin::Link */}
          <Link to='/auth/forgot-password' className='link-primary'>
            Forgot Password ?
          </Link>
          {/* end::Link */}
        </div>
        {/* end::Wrapper */}

        {/* begin::Action */}
        <div className='d-grid mb-10'>
          <button
            type='submit'
            id='kt_sign_in_submit'
            className='btn btn-primary'
            disabled={formik.isSubmitting || !formik.isValid}
          >
            {!loading && <span className='indicator-label'>Login</span>}
            {loading && (
              <span className='indicator-progress' style={{ display: 'block' }}>
                Please wait...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
        {/* end::Action */}
      </form>
    </FormikProvider>
  );
}
